// Utilidades para descargas múltiples

// Verificar si el navegador soporta File System Access API
export const supportsFileSystemAccess = () => {
  const supports = 'showDirectoryPicker' in window;
  console.log('File System Access API soportado:', supports);
  console.log('Navegador:', navigator.userAgent);
  console.log('Protocolo:', window.location.protocol);
  console.log('Hostname:', window.location.hostname);
  return supports;
};

// Descargar múltiples archivos usando File System Access API
export const downloadMultipleFilesWithPicker = async (files, getFileUrlFn) => {
  if (!supportsFileSystemAccess()) {
    throw new Error('File System Access API no soportado');
  }

  try {
    // Abrir el picker para seleccionar directorio
    const directoryHandle = await window.showDirectoryPicker({
      mode: 'readwrite'
    });

    let downloadedCount = 0;
    let failedCount = 0;
    const errors = [];

    // Descargar cada archivo al directorio seleccionado
    for (const file of files) {
      try {
        const fileName = file.Key || file.name;
        console.log(`Descargando: ${fileName}`);
        
        // Obtener la URL del archivo (forzar descarga)
        const fileUrl = await getFileUrlFn(fileName, true);
        if (!fileUrl) {
          throw new Error(`No se pudo obtener la URL para ${fileName}`);
        }

        // Fetch del archivo
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Error HTTP ${response.status} para ${fileName}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        
        // Crear el archivo en el directorio seleccionado
        const fileHandle = await directoryHandle.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(arrayBuffer);
        await writable.close();
        
        downloadedCount++;
        console.log(`✓ Descargado: ${fileName}`);
      } catch (error) {
        console.error(`Error descargando ${file.Key || file.name}:`, error);
        failedCount++;
        errors.push({
          fileName: file.Key || file.name,
          error: error.message
        });
      }
    }

    return {
      success: true,
      downloadedCount,
      failedCount,
      errors,
      message: `Se descargaron ${downloadedCount} archivos${failedCount > 0 ? ` (${failedCount} fallaron)` : ''}`
    };
  } catch (error) {
    return {
      success: false,
      downloadedCount: 0,
      failedCount: files.length,
      errors: [{ error: error.message }],
      message: 'Error general en la descarga múltiple'
    };
  }
};

// Descargar múltiples archivos usando método tradicional (fallback)
export const downloadMultipleFilesTraditional = async (files, getFileUrlFn) => {
  let downloadedCount = 0;
  let failedCount = 0;
  const errors = [];

  for (const file of files) {
    try {
      const fileName = file.Key || file.name;
      const fileUrl = await getFileUrlFn(fileName, true);
      
      if (fileUrl) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        downloadedCount++;
        
        // Pequeña pausa entre descargas para evitar bloqueos del navegador
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        throw new Error('No se pudo obtener la URL');
      }
    } catch (error) {
      console.error(`Error descargando ${file.Key || file.name}:`, error);
      failedCount++;
      errors.push({
        fileName: file.Key || file.name,
        error: error.message
      });
    }
  }

  return {
    success: true,
    downloadedCount,
    failedCount,
    errors,
    message: `Se iniciaron ${downloadedCount} descargas${failedCount > 0 ? ` (${failedCount} fallaron)` : ''}`
  };
};

// Función principal para descargar múltiples archivos
export const downloadMultipleFiles = async (files, getFileUrlFn, forceTraditional = false) => {
  if (!files || files.length === 0) {
    throw new Error('No hay archivos para descargar');
  }

  // Si solo es un archivo, usar descarga tradicional
  if (files.length === 1) {
    return await downloadMultipleFilesTraditional(files, getFileUrlFn);
  }

  // Para múltiples archivos, siempre intentar usar File System Access API primero
  if (!forceTraditional && supportsFileSystemAccess()) {
    try {
      const result = await downloadMultipleFilesWithPicker(files, getFileUrlFn);
      
      // Si hay error de seguridad, usar método tradicional automáticamente
      if (result.securityError) {
        console.warn('Error de permisos con File System Access API, usando método tradicional');
        return await downloadMultipleFilesTraditional(files, getFileUrlFn);
      }
      
      return result;
    } catch (error) {
      console.warn('Error con File System Access API, usando método tradicional:', error);
      return await downloadMultipleFilesTraditional(files, getFileUrlFn);
    }
  } else {
    console.log('File System Access API no disponible, usando descarga tradicional');
    return await downloadMultipleFilesTraditional(files, getFileUrlFn);
  }
}; 